<!-- ════════════════════════════ COMPACT — PILL ════════════════════════════ -->
@if (mode() === "compact") {
  <div
    class="qst-pill"
    [class.qst-pill--detour]="isInDrawingDetour()"
    [class.qst-pill--review]="isInReviewDetour()"
    [class.qst-pill--terminal]="isTerminal()"
    role="status"
    [pTooltip]="a11ySummary()"
    tooltipPosition="bottom"
  >
    @if (compactPrefixLabelKey(); as srcKey) {
      <span class="qst-pill__prefix">{{ srcKey | translate }}</span>
      <i class="pi pi-arrow-right qst-pill__arrow"></i>
    }
    <span class="qst-pill__dot" [class.qst-pill__dot--pulse]="isInAnyDetour() && !isTerminal()"></span>
    <span class="qst-pill__label">{{ currentLabelKey() | translate }}</span>
  </div>
}

<!-- ═══════════════════════════ EXPANDED — STEPPER ═══════════════════════════ -->
@if (mode() === "expanded") {
  <div
    class="qst-stepper"
    [class.qst-stepper--with-detour]="hasAnyDetour()"
    [class.qst-stepper--terminal]="isTerminal()"
    [style.--qst-cols]="cssVars()['--qst-cols']"
    [style.--qst-cols-total]="cssVars()['--qst-cols-total']"
    [style.--qst-max-row]="cssVars()['--qst-max-row']"
    role="group"
    [attr.aria-label]="'QUOTE.TIMELINE.ARIA_LABEL' | translate"
  >
    <p class="qst-sr-only">{{ a11ySummary() }}</p>

    <!-- ───── DETOUR LANES (above) — one arch per (kind, source column) ───── -->
    @for (ep of allEpisodes(); track ep.kind + ':' + ep.sourceIdx) {
      <div
        class="qst-detour"
        [class.qst-detour--drawing]="ep.kind === 'drawing'"
        [class.qst-detour--review]="ep.kind === 'review'"
        [class.qst-detour--active]="ep.state === 'active'"
        [class.qst-detour--completed]="ep.state === 'completed'"
        [class.qst-detour--future]="ep.state === 'future'"
        [class.qst-detour--at-entry]="ep.at === 'entry'"
        [class.qst-detour--at-progress]="ep.at === 'progress'"
        [style.--qst-source-col]="arxSourceCol(ep)"
        [style.--qst-episode-row]="ep.row"
      >
        <div class="qst-detour__frame qst-detour__frame--left" aria-hidden="true"></div>
        <div class="qst-detour__frame qst-detour__frame--right" aria-hidden="true"></div>

        <ol class="qst-detour__nodes">
          @for (n of ep.nodes; track n.status) {
            <li
              class="qst-detour__node"
              [class.qst-detour__node--completed]="n.state === 'completed'"
              [class.qst-detour__node--current]="n.state === 'current'"
              [class.qst-detour__node--future]="n.state === 'future'"
            >
              <span class="qst-detour__bubble">
                @if (n.state === "completed") {
                  <i class="pi pi-check"></i>
                } @else if (n.state === "current") {
                  <span class="qst-detour__bubble-dot"></span>
                } @else {
                  <i class="pi" [class]="n.icon"></i>
                }
              </span>
              <span class="qst-detour__label">{{ n.labelKey | translate }}</span>
              @if (n.date) {
                <time class="qst-detour__date" [attr.datetime]="n.date">
                  {{ n.date | CoreCompanyDate }} {{ n.date | date:'HH:mm' }}
                </time>
              }
            </li>
          }
        </ol>
      </div>
    }

    <!-- ───── MAIN PATH ───── -->
    <ol class="qst-main">
      @for (n of mainNodes(); track n.status; let i = $index; let last = $last) {
        <li
          class="qst-main__node"
          [class.qst-main__node--completed]="n.state === 'completed'"
          [class.qst-main__node--current]="n.state === 'current'"
          [class.qst-main__node--future]="n.state === 'future'"
          [attr.aria-current]="n.state === 'current' ? 'step' : null"
        >
          @if (!last) {
            <span
              class="qst-main__connector"
              [class.qst-main__connector--completed]="mainNodes()[i + 1].state === 'completed' || mainNodes()[i + 1].state === 'current'"
              [class.qst-main__connector--paused]="isInAnyDetour() && n.state === 'current'"
              aria-hidden="true"
            ></span>
          }
          <span class="qst-main__bubble">
            @if (n.state === "completed") {
              <i class="pi pi-check"></i>
            } @else if (n.state === "current") {
              <span class="qst-main__bubble-dot"></span>
              <span class="qst-main__bubble-pulse" aria-hidden="true"></span>
            } @else {
              <i class="pi" [class]="n.icon"></i>
            }
          </span>
          <span class="qst-main__label">{{ n.labelKey | translate }}</span>
          @if (n.date) {
            <time class="qst-main__date" [attr.datetime]="n.date">
              {{ n.date | CoreCompanyDate }} {{ n.date | date:'HH:mm' }}
            </time>
          }
        </li>
      }
      @if (terminator(); as t) {
        <li class="qst-main__node qst-main__node--terminal">
          <span class="qst-main__connector qst-main__connector--terminal" aria-hidden="true"></span>
          <span class="qst-main__bubble qst-main__bubble--terminal"><i class="pi pi-times"></i></span>
          <span class="qst-main__label">{{ t.labelKey | translate }}</span>
        </li>
      }
    </ol>

    <!-- ───── DETOUR META (assignee + ETA) — drawing only ───── -->
    @if (isInDrawingDetour() && drawerName()) {
      <div class="qst-meta">
        <span class="qst-meta__label">{{ "QUOTE.DRAWING.ASSIGNEE" | translate }}</span>
        <span class="qst-meta__value">{{ drawerName() }}</span>
        @if (estimatedCompletion(); as d) {
          <span class="qst-meta__separator">·</span>
          <span class="qst-meta__label">{{ "QUOTE.DRAWING.ESTIMATED_COMPLETION" | translate }}</span>
          <time class="qst-meta__value" [attr.datetime]="d">{{ d | CoreCompanyDate }}</time>
        }
      </div>
    }
  </div>
}
